import React, { useState, useEffect } from 'react';
import HeaderContainer from '../core/HeadersAndFooters/HeaderContainer';
import HeaderButtonsForm from '../core/HeadersAndFooters/HeaderButtonsForm';
import FooterContainer from '../core/HeadersAndFooters/FooterContainer';
import { Image as ImageIcon, ArrowLeft } from 'lucide-react';
import { useMediaQuery } from '../../hooks/useMediaQuery';
import { useTheme } from '../../themes/ThemeProvider';
import { useTranslation } from '../../i18n/useTranslation';

// Import reliable fetch helper from shared location
import { supabaseFetch } from '../../lib/supabase';
import { useAuth } from '../../contexts/AuthContext';
import { groupService } from '../../data/dataService';
import JoinRequestModal from './JoinRequestModal';
import InvitationOutcome, { goHome, goToGroup } from './InvitationOutcome';
import { formatTextWithStyles } from '../../utils/formatText';
import { rateLimitMessage } from '../../utils/rateLimitMessage';

/**
 * LandingPageGroup - Read-only display of a group's landing page (its Introduction page)
 * Based on G2_GROUPS_INFO-FRONT_PAGE_D.pdf design
 * Two-column layout with inline images support
 *
 * Details and Introduction text come from get_group_introduction (20261004_group_types), the one
 * data path: a public or findable group to anyone; an invitation-only group to its members and to
 * the holder of a valid invitation or live join link (addendum 4). The page must read well with no
 * Introduction text (addendum 4 item 3): the text columns are then left out.
 *
 * `invitation` ({ kind: 'invite' | 'link', token }) is "arrived with an invitation" mode (addendum
 * 3 Q1, addendum 4 Q1a): mounted inside the invitation routes (AcceptInvitation / JoinGroupLink),
 * so the token never enters app-level state. Join then accepts the invitation or link directly —
 * the invitation is the admin's consent — and the result is the invitation outcome page.
 */
const LandingPageGroup = ({ onBack, group, currentUser, onContinueToGroup, invitation = null, onShowLogin }) => {
  const theme = useTheme();
  const { t } = useTranslation('groups');
  const { isMobile } = useMediaQuery();
  const [loading, setLoading] = useState(true);

  // What get_group_introduction says this reader may see (null until read).
  const [details, setDetails] = useState(null);
  const [inviteResult, setInviteResult] = useState(null);   // { code, groupId, groupName }
  const [accepting, setAccepting] = useState(false);

  // Join label: mirrors GroupPrivateGate.jsx's idiom — driven by auto_accept_members, not
  // is_private (the two are independent; see DATABASE.md § group_join_requests). Both private
  // types never admit anyone directly (addendum 5/6); a public group's approval is the owner's.
  const accessType = details?.found ? details.accessType : (group?.group_access || null);
  const autoAccept = accessType && accessType !== 'public' ? false : group?.auto_accept_members !== false;
  // No request route into an invitation-only group (R1): only an invitation or link joins it.
  const canAskToJoin = !!invitation || accessType !== 'private_invitation';

  // group (landingPageGroup) may carry whatever narrow `select` list its source card/dispatch
  // used — is_federation is a non-null boolean column, so `undefined` here can only mean a
  // `select` omitted it. Mirrors useGroupHome.js's completeGroup upgrade-fetch (3b1caa7e) and
  // GroupPrivateGate.jsx's identical guard: never gate the join CTA on a partial group object.
  const [isFederation, setIsFederation] = useState(group?.is_federation);
  useEffect(() => {
    setIsFederation(group?.is_federation);
    if (!group?.id || group.is_federation !== undefined) return;
    let cancelled = false;
    supabaseFetch.query('communities', {
      select: 'is_federation',
      eq: { id: group.id },
      single: true
    }).then(({ data, error }) => {
      if (cancelled || error || !data) return;
      setIsFederation(data.is_federation);
    });
    return () => { cancelled = true; };
  }, [group?.id, group?.is_federation]);

  // Logo click listener - navigate back to MainFeed
  useEffect(() => {
    const handleLogoClick = () => {
      if (onBack) onBack();
    };
    window.addEventListener('ournet:context-switch', handleLogoClick);
    return () => window.removeEventListener('ournet:context-switch', handleLogoClick);
  }, [onBack]);

  // Content state
  const [leftColumnContent, setLeftColumnContent] = useState('');
  const [rightColumnContent, setRightColumnContent] = useState('');

  // Inline images state
  const [leftInlineImages, setLeftInlineImages] = useState([]);
  const [rightInlineImages, setRightInlineImages] = useState([]);

  // Membership state
  const { requireAuth, isLoggedIn } = useAuth();
  const [membershipStatus, setMembershipStatus] = useState({
    isMember: false,
    role: null,
    isOwner: false,
    hasPendingRequest: false,
    loading: true
  });
  const [showJoinModal, setShowJoinModal] = useState(false);
  const [joinLoading, setJoinLoading] = useState(false);
  const [joinMessage, setJoinMessage] = useState('');
  const [joinOk, setJoinOk] = useState(false);
  const showMessage = (text, ok) => { setJoinOk(ok); setJoinMessage(text); };

  const userId = currentUser?.id;

  const groupName = (details?.found && details.name) || group?.name || '';
  const heroUrl = (details?.found && details.heroUrl)
    || group?.hero_image_display_url || group?.cover_image_url || group?.logo_url || null;
  // The logo is shown beside the name only when it is a different picture from the hero.
  const logoUrl = details?.found && details.logoUrl && details.logoUrl !== heroUrl ? details.logoUrl : null;
  const place = details?.found ? details.place : '';
  const description = details?.found ? details.description : (group?.description || '');

  // Page title
  const pageTitle = groupName ? t('intro_page_title', { group: groupName }) : t('intro_page_title_no_name');

  // Check membership status when group changes
  useEffect(() => {
    const checkMembershipStatus = async () => {
      if (!group?.id) {
        setMembershipStatus({ isMember: false, role: null, isOwner: false, hasPendingRequest: false, loading: false });
        return;
      }

      if (!isLoggedIn || !userId) {
        setMembershipStatus({ isMember: false, role: null, isOwner: false, hasPendingRequest: false, loading: false });
        return;
      }

      setMembershipStatus(prev => ({ ...prev, loading: true }));

      // Safety timeout - if membership check hangs, default to not-a-member after 5s
      const timeout = setTimeout(() => {
        console.warn('Membership check timed out, defaulting to non-member');
        setMembershipStatus(prev => {
          if (prev.loading) {
            return { isMember: false, role: null, isOwner: group.owner_id === userId, hasPendingRequest: false, loading: false };
          }
          return prev;
        });
      }, 5000);

      try {
        const { isMember, role } = await groupService.checkMembership(userId, group.id);
        // The membership role too: in invitation mode the group prop carries no owner_id.
        const isOwner = group.owner_id === userId || role === 'owner';
        const pendingRequest = await groupService.getJoinRequestStatus(userId, group.id);
        const hasPendingRequest = pendingRequest?.status === 'pending';

        clearTimeout(timeout);
        setMembershipStatus({
          isMember: isMember || isOwner,
          role: isOwner ? 'owner' : role,
          isOwner,
          hasPendingRequest,
          loading: false
        });
      } catch (error) {
        clearTimeout(timeout);
        console.error('Error checking membership:', error);
        setMembershipStatus({ isMember: false, role: null, isOwner: false, hasPendingRequest: false, loading: false });
      }
    };

    checkMembershipStatus();
  }, [group?.id, group?.owner_id, userId, isLoggedIn]);

  // Arrived with an invitation: Join accepts it directly (the invitation is the consent).
  const handleAcceptInvitation = async () => {
    if (!userId) {
      onShowLogin?.(t('join_sign_in_prompt'));
      return;
    }
    setAccepting(true);
    if (invitation.kind === 'invite') {
      const res = await groupService.acceptInvitation(invitation.token, userId);
      setInviteResult({ kind: 'invite', code: res.code, groupId: res.groupId || group?.id, groupName: res.groupName || groupName });
    } else {
      const res = await groupService.acceptGroupJoinLink(invitation.token);
      setInviteResult({
        kind: 'link',
        code: res.outcome === 'error' ? 'failed' : res.outcome,
        groupId: res.groupId || group?.id,
        groupName: res.groupName || groupName
      });
    }
    setAccepting(false);
  };

  // Handle join button click
  const handleJoinClick = () => {
    if (invitation) {
      handleAcceptInvitation();
      return;
    }
    requireAuth('gate_body_join_group', () => {
      if (!autoAccept) {
        setShowJoinModal(true);
      } else {
        handleJoinGroup(null);
      }
    });
  };

  // Handle join group
  const handleJoinGroup = async (message) => {
    setJoinLoading(true);
    try {
      const result = await groupService.joinGroup(userId, group.id, message);

      if (result.success) {
        if (result.status === 'joined') {
          setMembershipStatus(prev => ({ ...prev, isMember: true, role: 'member' }));
          showMessage(t('intro_joined', { group: result.groupName || groupName }), true);
        } else if (result.status === 'pending') {
          setMembershipStatus(prev => ({ ...prev, hasPendingRequest: true }));
          showMessage(t('intro_request_sent', { group: result.groupName || groupName }), true);
        }
      } else if (result.status === 'blocked') {
        showMessage(t('cannot_request_to_join'), false);
      } else if (result.status === 'rate_limited') {
        showMessage(rateLimitMessage(result.error, t) || t('common:rate_limit_generic'), false);
      } else if (result.status === 'pending') {
        setMembershipStatus(prev => ({ ...prev, hasPendingRequest: true }));
      } else {
        // joinGroup's own English is never shown (TRANSLATION § Principle).
        showMessage(t('intro_join_failed'), false);
      }
    } catch (error) {
      console.error('Error joining group:', error);
      showMessage(t('intro_error'), false);
    } finally {
      setJoinLoading(false);
      setShowJoinModal(false);
      setTimeout(() => setJoinMessage(''), 3000);
    }
  };

  // Handle cancel request
  const handleCancelRequest = async () => {
    setJoinLoading(true);
    try {
      const success = await groupService.cancelJoinRequest(userId, group.id);
      if (success) {
        setMembershipStatus(prev => ({ ...prev, hasPendingRequest: false }));
        showMessage(t('intro_request_cancelled'), true);
        setTimeout(() => setJoinMessage(''), 3000);
      }
    } catch (error) {
      console.error('Error canceling request:', error);
    } finally {
      setJoinLoading(false);
    }
  };

  // Handle leave group
  const handleLeaveGroup = async () => {
    setJoinLoading(true);
    try {
      const success = await groupService.leaveGroup(userId, group.id);
      if (success) {
        setMembershipStatus(prev => ({ ...prev, isMember: false, role: null }));
        showMessage(t('intro_left_group', { group: groupName }), true);
        setTimeout(() => setJoinMessage(''), 3000);
      } else {
        showMessage(t('intro_leave_failed'), false);
        setTimeout(() => setJoinMessage(''), 3000);
      }
    } catch (error) {
      console.error('Error leaving group:', error);
      showMessage(t('intro_error'), false);
      setTimeout(() => setJoinMessage(''), 3000);
    } finally {
      setJoinLoading(false);
    }
  };

  // Load existing landing page data (or mock data for dev preview)
  useEffect(() => {
    if (!group?.id) return;

    // Dev preview mode: populate with sample content, skip database
    if (group._isDevPreview) {
      setLeftColumnContent(
        'Welcome to our community! We are a passionate group of enthusiasts dedicated to exploring and celebrating the rich wine culture of Portugal\'s Silver Coast region.\n\n' +
        'Our group organises regular tastings, vineyard tours, and educational events throughout the Oeste region. Whether you\'re a seasoned sommelier or just beginning your wine journey, there\'s a place for you here.\n\n' +
        '[IMG:preview-left-1]\n\n' +
        'We meet every second Saturday at rotating venues across Caldas da Rainha, Obidos, and Alcobaca. Our members include local winemakers, restaurant owners, and wine lovers from all walks of life.\n\n' +
        'Recent highlights include our harvest festival in October, where over 50 members participated in grape picking at Quinta da Boa Vista, followed by a communal dinner featuring dishes paired with local wines.\n\n' +
        '[IMG:preview-left-2]\n\n' +
        'Join us to discover hidden gems among the vineyards of the Silver Coast. From the mineral-rich whites of the Obidos DOC to the full-bodied reds of Alcobaca, there is always something new to taste and learn.'
      );
      setRightColumnContent(
        'Upcoming Events\n\n' +
        '• Wine & Cheese Evening — March 15\nHosted at Casa do Vinho, Caldas da Rainha. Featuring five local producers.\n\n' +
        '• Vineyard Walk — April 2\nGuided tour of Quinta do Sanguinhal with tasting of their reserve collection.\n\n' +
        '[IMG:preview-right-1]\n\n' +
        'How to Join\n\n' +
        'Membership is open to everyone with an interest in wine. Annual dues are €25 and include access to all regular events, our private tasting notes library, and discounts at partner restaurants.\n\n' +
        'Contact us through the group chat or attend any of our open events to learn more.\n\n' +
        'Group Rules\n\n' +
        '1. Respect all members and their opinions\n2. No commercial promotion without approval\n3. Drink responsibly at all events\n4. Share your knowledge generously'
      );
      setLeftInlineImages([
        { id: 'preview-left-1', displayUrl: 'https://placehold.co/800x400/BB4D9A/white?text=Vineyard+Panorama', size: 'L' },
        { id: 'preview-left-2', displayUrl: 'https://placehold.co/400x300/024B99/white?text=Wine+Tasting', size: 'M' }
      ]);
      setRightInlineImages([
        { id: 'preview-right-1', displayUrl: 'https://placehold.co/300x200/F9A61B/white?text=Upcoming+Event', size: 'L' }
      ]);
      setLoading(false);
      return;
    }

    let cancelled = false;
    const loadLandingPage = async () => {
      setLoading(true);
      // Never log the token or the address: an error report would store it (addendum 3 Q2).
      const result = await groupService.getGroupIntroduction(group.id, invitation?.token || null);
      if (cancelled) return;
      setDetails(result);
      if (result?.found) {
        setLeftColumnContent(result.introLeft);
        setRightColumnContent(result.introRight);
        setLeftInlineImages(result.introLeftImages || []);
        setRightInlineImages(result.introRightImages || []);
      } else {
        setLeftColumnContent('');
        setRightColumnContent('');
        setLeftInlineImages([]);
        setRightInlineImages([]);
      }
      setLoading(false);
    };

    loadLandingPage();
    return () => { cancelled = true; };
  }, [group?.id, group?._isDevPreview, invitation?.token]); // eslint-disable-line react-hooks/exhaustive-deps

  // Image size options: S = 33%, M = 66%, L = 100%
  const IMAGE_SIZES = {
    S: '33%',
    M: '66%',
    L: '100%'
  };

  // Render content with inline images (with size support)
  const renderContentWithImages = (content, images) => {
    if (!content) return null;

    // Split content by image markers
    const parts = content.split(/(\[IMG:[^\]]+\])/g);

    return parts.map((part, index) => {
      // Check if this is an image marker
      const imageMatch = part.match(/\[IMG:([^\]]+)\]/);
      if (imageMatch) {
        const imageId = imageMatch[1];
        const image = images.find(img => img.id === imageId);
        if (image) {
          const imageWidth = IMAGE_SIZES[image.size] || '100%';
          return (
            <div key={index} style={{ margin: '16px 0', position: 'relative' }}>
              <img
                src={image.displayUrl}
                alt=""
                loading="lazy"
                style={{
                  width: imageWidth,
                  borderRadius: '8px',
                  objectFit: 'cover'
                }}
              />
            </div>
          );
        }
        return null;
      }

      // Regular text content — through the articles' formatter (bold, italic, headings; React
      // text only, never HTML), keeping the line breaks the plain text had (addendum 2 item 2).
      if (part.trim()) {
        return (
          <React.Fragment key={index}>
            {formatTextWithStyles(part, `intro-${index}-`, {
              fontSize: '14px',
              lineHeight: '1.6',
              color: '#374151',
              whiteSpace: 'pre-wrap',
              marginBottom: '12px'
            })}
          </React.Fragment>
        );
      }
      return null;
    });
  };

  if (loading) {
    return (
      <div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <p style={{ color: '#6b7280' }}>{t('intro_loading')}</p>
      </div>
    );
  }

  // Arrived with an invitation and pressed Join: the invitation outcome page, as before.
  if (inviteResult) {
    return (
      <InvitationOutcome
        kind={inviteResult.kind}
        code={inviteResult.code}
        groupId={inviteResult.groupId}
        groupName={inviteResult.groupName}
      />
    );
  }

  const hasIntroText = !!(leftColumnContent || rightColumnContent);
  // Missing, being deleted, or hidden from this reader: one answer, by design.
  const cannotShow = !!details && !details.found && !details.error && !group?._isDevPreview;
  const backAction = invitation ? goHome : onBack;
  const continueAction = invitation
    ? () => goToGroup(group?.id)
    : () => onContinueToGroup && onContinueToGroup(group);

  return (
    <div style={{ minHeight: '100vh', backgroundColor: '#EDF2F8', display: 'flex', flexDirection: 'column' }}>
      {/* Header - Mobile: compact 50px, Desktop: HeaderContainer */}
      {isMobile ? (
        <header style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          height: '50px',
          backgroundColor: 'white',
          display: 'flex',
          alignItems: 'center',
          padding: '0 12px',
          zIndex: 1000,
          boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
          gap: '12px'
        }}>
          <button
            onClick={backAction}
            style={{
              background: 'none',
              border: 'none',
              cursor: 'pointer',
              padding: '4px',
              display: 'flex',
              alignItems: 'center'
            }}
          >
            <ArrowLeft size={24} color={theme?.primary || '#BB4D9A'} />
          </button>
          <span style={{
            fontSize: '15px',
            fontWeight: 'bold',
            color: theme?.primary || '#BB4D9A',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
            flex: 1
          }}>
            {pageTitle}
          </span>
        </header>
      ) : (
        <>
          <HeaderContainer />
          <HeaderButtonsForm
            currentContext={{ name: pageTitle }}
            onBack={backAction}
          />
        </>
      )}

      {/* Main Content */}
      <div style={{
        flex: 1,
        paddingTop: isMobile ? '58px' : '90px',
        paddingBottom: isMobile ? '80px' : '40px',
        overflowY: 'auto'
      }}>
        <div style={{
          maxWidth: '1400px',
          margin: '0 auto',
          padding: isMobile ? '0 16px' : '0 40px'
        }}>

          {/* Hero Section - Group Image and Title */}
          <div style={{
            display: 'flex',
            flexDirection: isMobile ? 'column' : 'row',
            gap: isMobile ? '16px' : '40px',
            marginBottom: isMobile ? '20px' : '40px',
            alignItems: isMobile ? 'center' : 'flex-start'
          }}>
            {/* Group Image */}
            <div style={{
              width: isMobile ? '160px' : '300px',
              flexShrink: 0,
              position: 'relative'
            }}>
              <div style={{
                width: '100%',
                aspectRatio: '1 / 1',
                borderRadius: isMobile ? '12px' : '16px',
                overflow: 'hidden',
                backgroundColor: '#e5e7eb',
                position: 'relative'
              }}>
                {heroUrl ? (
                  <img
                    src={heroUrl}
                    alt={groupName}
                    loading="lazy"
                    style={{
                      width: '100%',
                      height: '100%',
                      objectFit: 'cover'
                    }}
                  />
                ) : (
                  <div style={{
                    width: '100%',
                    height: '100%',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    backgroundColor: theme?.primary || '#BB4D9A'
                  }}>
                    <ImageIcon size={isMobile ? 40 : 64} color="white" />
                  </div>
                )}
              </div>
            </div>

            {/* Group Title */}
            <div style={{
              flex: 1,
              paddingTop: isMobile ? '0' : '20px',
              textAlign: isMobile ? 'center' : 'left'
            }}>
              <h1 style={{
                fontSize: isMobile ? '28px' : '48px',
                fontWeight: '700',
                color: theme?.primary || '#BB4D9A',
                lineHeight: '1.1',
                marginBottom: isMobile ? '8px' : '16px'
              }}>
                {logoUrl && (
                  <img
                    src={logoUrl}
                    alt=""
                    style={{ width: isMobile ? '32px' : '44px', height: isMobile ? '32px' : '44px', borderRadius: '8px', objectFit: 'cover', verticalAlign: 'middle', marginRight: '12px' }}
                  />
                )}
                {groupName}
              </h1>
              {place && (
                <p style={{ fontSize: isMobile ? '13px' : '14px', color: '#374151', fontWeight: '600', marginBottom: '8px' }}>
                  {place}
                </p>
              )}
              {description && (
                <p style={{
                  fontSize: isMobile ? '13px' : '14px',
                  color: '#6b7280',
                  lineHeight: '1.6'
                }}>
                  {description}
                </p>
              )}
              {cannotShow && (
                <p style={{ fontSize: '14px', color: '#6b7280', lineHeight: '1.6' }}>
                  {t('gate_cannot_show_or_invitation_only')}
                </p>
              )}
            </div>
          </div>

          {/* Two Column Content Area - stacks on mobile. Left out entirely when the group has
              written no Introduction (addendum 4 item 3: 22 of 23 groups have none). */}
          {hasIntroText && (
          <div style={{
            display: 'flex',
            flexDirection: isMobile ? 'column' : 'row',
            gap: isMobile ? '16px' : '40px'
          }}>
            {/* Left Column - 65% desktop, full width mobile */}
            <div style={{ flex: isMobile ? '1' : '0 0 65%' }}>
              <div style={{
                backgroundColor: 'white',
                borderRadius: '12px',
                padding: isMobile ? '16px' : '24px',
                border: '1px solid #e5e7eb'
              }}>
                {renderContentWithImages(leftColumnContent, leftInlineImages)}
              </div>
            </div>

            {/* Right Column - 35% desktop, full width mobile */}
            <div style={{ flex: isMobile ? '1' : '0 0 35%' }}>
              <div style={{
                backgroundColor: 'white',
                borderRadius: '12px',
                padding: isMobile ? '16px' : '24px',
                border: '1px solid #e5e7eb'
              }}>
                {renderContentWithImages(rightColumnContent, rightInlineImages)}
              </div>
            </div>
          </div>
          )}

        </div>
      </div>

      {/* Join Request Modal (for groups requiring approval) */}
      {showJoinModal && (
        <JoinRequestModal
          isOpen={showJoinModal}
          onClose={() => setShowJoinModal(false)}
          onSubmit={handleJoinGroup}
          groupName={group?.name}
          loading={joinLoading}
        />
      )}

      {/* Footer with Join/Leave/Owner Button */}
      {isMobile ? (
        <div style={{
          position: 'fixed',
          bottom: 0,
          left: 0,
          right: 0,
          height: '60px',
          backgroundColor: 'rgba(255,255,255,0.95)',
          backdropFilter: 'blur(8px)',
          WebkitBackdropFilter: 'blur(8px)',
          boxShadow: '0 -2px 8px rgba(0,0,0,0.1)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 999,
          padding: '0 12px',
          gap: '8px'
        }}>
          {!membershipStatus.loading && group && !joinMessage && (
            <>
              {/* Back button on mobile footer */}
              <button
                onClick={backAction}
                style={{
                  padding: '8px 16px',
                  background: 'white',
                  color: theme?.primary || '#BB4D9A',
                  border: `2px solid ${theme?.primary || '#BB4D9A'}`,
                  borderRadius: '20px',
                  fontSize: '13px',
                  fontWeight: '600',
                  cursor: 'pointer'
                }}
              >
                {t('intro_back')}
              </button>

              {/* Join/Leave/Status button — the join/request-to-join family is never offered on
                  a federation (4C §5: federates groups, not people). Owner/Leave below reflect
                  existing membership, not an offer, so they stay unconditional. */}
              {!isFederation && canAskToJoin && !isLoggedIn && (
                <button
                  onClick={handleJoinClick}
                  style={{
                    padding: '8px 20px',
                    background: theme?.primary || '#BB4D9A',
                    color: 'white',
                    border: 'none',
                    borderRadius: '20px',
                    fontSize: '13px',
                    fontWeight: '600',
                    cursor: 'pointer'
                  }}
                >
                  {invitation ? t('join_sign_in') : t('login_to_join')}
                </button>
              )}

              {!isFederation && canAskToJoin && isLoggedIn && !membershipStatus.isMember && (!membershipStatus.hasPendingRequest || invitation) && (
                <button
                  onClick={handleJoinClick}
                  disabled={joinLoading || accepting}
                  style={{
                    padding: '8px 20px',
                    background: theme?.primary || '#BB4D9A',
                    color: 'white',
                    border: 'none',
                    borderRadius: '20px',
                    fontSize: '13px',
                    fontWeight: '600',
                    cursor: joinLoading ? 'wait' : 'pointer',
                    opacity: joinLoading ? 0.7 : 1
                  }}
                >
                  {invitation
                    ? (accepting ? t('join_joining') : t('join_button'))
                    : (joinLoading ? t('ui:joining') : (autoAccept ? t('ui:join') : t('request_to_join')))}
                </button>
              )}

              {!isFederation && !invitation && isLoggedIn && !membershipStatus.isMember && membershipStatus.hasPendingRequest && (
                <div style={{
                  padding: '8px 12px',
                  background: '#F59E0B',
                  color: 'white',
                  borderRadius: '20px',
                  fontSize: '12px',
                  fontWeight: '600'
                }}>
                  {t('intro_pending')}
                </div>
              )}

              {isLoggedIn && membershipStatus.isMember && membershipStatus.isOwner && (
                <div style={{
                  padding: '8px 12px',
                  background: '#5DBEA3',
                  color: 'white',
                  borderRadius: '20px',
                  fontSize: '12px',
                  fontWeight: '600'
                }}>
                  {t('intro_owner')}
                </div>
              )}

              {isLoggedIn && membershipStatus.isMember && !membershipStatus.isOwner && (
                <button
                  onClick={handleLeaveGroup}
                  disabled={joinLoading || accepting}
                  style={{
                    padding: '8px 20px',
                    background: '#5DBEA3',
                    color: 'white',
                    border: 'none',
                    borderRadius: '20px',
                    fontSize: '13px',
                    fontWeight: '600',
                    cursor: joinLoading ? 'wait' : 'pointer',
                    opacity: joinLoading ? 0.7 : 1
                  }}
                >
                  {joinLoading ? t('intro_leaving') : t('ui:leave')}
                </button>
              )}

              {/* Continue to Group — with an invitation, only once in (else the gate would stop them) */}
              {(!invitation || membershipStatus.isMember) && (
              <button
                onClick={continueAction}
                style={{
                  padding: '8px 16px',
                  background: 'white',
                  color: theme?.primary || '#BB4D9A',
                  border: `2px solid ${theme?.primary || '#BB4D9A'}`,
                  borderRadius: '20px',
                  fontSize: '13px',
                  fontWeight: '600',
                  cursor: 'pointer'
                }}
              >
                {t('intro_continue')}
              </button>
              )}
            </>
          )}
          {joinMessage && (
            <div style={{
              padding: '8px 16px',
              background: joinOk ? '#5DBEA3' : '#EF4444',
              color: 'white',
              borderRadius: '8px',
              fontSize: '13px'
            }}>
              {joinMessage}
            </div>
          )}
        </div>
      ) : (
        <FooterContainer>
          {!membershipStatus.loading && group && (
            <div style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              height: '100%',
              gap: '8px'
            }}>
              {/* Join Message Toast */}
              {joinMessage && (
                <div style={{
                  padding: '8px 16px',
                  background: joinOk ? '#5DBEA3' : '#EF4444',
                  color: 'white',
                  borderRadius: '8px',
                  fontSize: '13px'
                }}>
                  {joinMessage}
                </div>
              )}

              {/* Visitor (not logged in) on a PRIVATE group — join family suppressed on a
                  federation (4C §5), same reasoning as the mobile footer above. */}
              {!isFederation && canAskToJoin && !isLoggedIn && group.is_private && !invitation && !joinMessage && (
                <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                  <div style={{
                    padding: '10px 16px',
                    background: '#6b7280',
                    color: 'white',
                    borderRadius: '25px',
                    fontSize: '14px',
                    fontWeight: '600'
                  }}>
                    {t('ui:private_group')}
                  </div>
                  <button
                    onClick={handleJoinClick}
                    style={{
                      padding: '10px 20px',
                      background: theme?.primary || '#BB4D9A',
                      color: 'white',
                      border: 'none',
                      borderRadius: '20px',
                      fontSize: '14px',
                      fontWeight: '600',
                      cursor: 'pointer',
                      transition: 'all 0.2s ease'
                    }}
                    onMouseOver={(e) => e.currentTarget.style.opacity = '0.9'}
                    onMouseOut={(e) => e.currentTarget.style.opacity = '1'}
                  >
                    {invitation ? t('join_sign_in') : t('login_to_join')}
                  </button>
                </div>
              )}

              {/* Visitor (not logged in) on a PUBLIC group */}
              {!isFederation && canAskToJoin && !isLoggedIn && (!group.is_private || invitation) && !joinMessage && (
                <button
                  onClick={handleJoinClick}
                  style={{
                    padding: '12px 32px',
                    background: theme?.primary || '#BB4D9A',
                    color: 'white',
                    border: 'none',
                    borderRadius: '25px',
                    fontSize: '15px',
                    fontWeight: '600',
                    cursor: 'pointer',
                    transition: 'all 0.2s ease'
                  }}
                  onMouseOver={(e) => e.currentTarget.style.opacity = '0.9'}
                  onMouseOut={(e) => e.currentTarget.style.opacity = '1'}
                >
                  {invitation ? t('join_sign_in') : t('login_to_join')}
                </button>
              )}

              {/* Logged in but not a member */}
              {!isFederation && canAskToJoin && isLoggedIn && !membershipStatus.isMember && (!membershipStatus.hasPendingRequest || invitation) && !joinMessage && (
                <button
                  onClick={handleJoinClick}
                  disabled={joinLoading || accepting}
                  style={{
                    padding: '12px 32px',
                    background: theme?.primary || '#BB4D9A',
                    color: 'white',
                    border: 'none',
                    borderRadius: '25px',
                    fontSize: '15px',
                    fontWeight: '600',
                    cursor: joinLoading ? 'wait' : 'pointer',
                    opacity: joinLoading ? 0.7 : 1,
                    transition: 'all 0.2s ease'
                  }}
                  onMouseOver={(e) => { if (!joinLoading) e.currentTarget.style.opacity = '0.9'; }}
                  onMouseOut={(e) => { if (!joinLoading) e.currentTarget.style.opacity = '1'; }}
                >
                  {invitation
                    ? (accepting ? t('join_joining') : t('join_button'))
                    : (joinLoading ? t('ui:joining') : (autoAccept ? t('ui:join') : t('request_to_join')))}
                </button>
              )}

              {/* Logged in with pending request */}
              {!isFederation && !invitation && isLoggedIn && !membershipStatus.isMember && membershipStatus.hasPendingRequest && !joinMessage && (
                <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                  <div style={{
                    padding: '10px 16px',
                    background: '#F59E0B',
                    color: 'white',
                    borderRadius: '25px',
                    fontSize: '14px',
                    fontWeight: '600'
                  }}>
                    {t('intro_request_pending')}
                  </div>
                  <button
                    onClick={handleCancelRequest}
                    disabled={joinLoading || accepting}
                    style={{
                      padding: '6px 12px',
                      background: 'transparent',
                      color: '#EF4444',
                      border: '1px solid #EF4444',
                      borderRadius: '15px',
                      fontSize: '12px',
                      cursor: 'pointer'
                    }}
                  >
                    {t('intro_cancel_request')}
                  </button>
                </div>
              )}

              {/* Logged in owner */}
              {isLoggedIn && membershipStatus.isMember && membershipStatus.isOwner && !joinMessage && (
                <div style={{
                  padding: '8px 16px',
                  background: '#5DBEA3',
                  color: 'white',
                  borderRadius: '20px',
                  fontSize: '13px',
                  fontWeight: '600',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '6px'
                }}>
                  <span>✓</span>
                  <span>{t('intro_owner')}</span>
                </div>
              )}

              {/* Logged in member (not owner) - Leave button */}
              {isLoggedIn && membershipStatus.isMember && !membershipStatus.isOwner && !joinMessage && (
                <button
                  onClick={handleLeaveGroup}
                  disabled={joinLoading || accepting}
                  style={{
                    padding: '12px 32px',
                    background: '#5DBEA3',
                    color: 'white',
                    border: 'none',
                    borderRadius: '25px',
                    fontSize: '15px',
                    fontWeight: '600',
                    cursor: joinLoading ? 'wait' : 'pointer',
                    opacity: joinLoading ? 0.7 : 1,
                    transition: 'all 0.2s ease'
                  }}
                  onMouseOver={(e) => { if (!joinLoading) e.currentTarget.style.opacity = '0.9'; }}
                  onMouseOut={(e) => { if (!joinLoading) e.currentTarget.style.opacity = '1'; }}
                >
                  {joinLoading ? t('intro_leaving') : t('intro_leave_group')}
                </button>
              )}

              {/* Continue to Group button */}
              {!joinMessage && (!invitation || membershipStatus.isMember) && (
                <button
                  onClick={continueAction}
                  style={{
                    padding: '12px 32px',
                    background: 'white',
                    color: theme?.primary || '#BB4D9A',
                    border: `2px solid ${theme?.primary || '#BB4D9A'}`,
                    borderRadius: '25px',
                    fontSize: '15px',
                    fontWeight: '600',
                    cursor: 'pointer',
                    transition: 'all 0.2s ease'
                  }}
                  onMouseOver={(e) => e.currentTarget.style.opacity = '0.9'}
                  onMouseOut={(e) => e.currentTarget.style.opacity = '1'}
                >
                  {t('intro_continue_to_group')}
                </button>
              )}
            </div>
          )}
        </FooterContainer>
      )}
    </div>
  );
};

export default LandingPageGroup;
